import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, FiUser, FiCalendar, FiDollarSign, FiMapPin, 
  FiCheckCircle, FiTruck, FiStar, FiPlus, FiTrash2, FiEdit2, 
  FiClock, FiRefreshCw, FiMessageSquare, FiActivity, FiArrowRight,
  FiBriefcase, FiTag, FiGift, FiBell, FiPrinter, FiMail
} from 'react-icons/fi';
import { getOrderById, updateOrderStatus, assignOrder, removeAssignment, updateAssignment, addOrderNote, sendOrderReminder } from '../services/orders';
import { getDrivers } from '../services/drivers';
import { getTourGuides } from '../services/tourGuides';
import { getCompanies } from '../services/companies';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import AssignModal from '../components/Orders/AssignModal';
import toast from 'react-hot-toast';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  assigned: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const assignmentTypeIcons = {
  driver: <FiTruck className="w-4 h-4" />,
  tour_guide: <FiStar className="w-4 h-4" />,
  tour_company: <FiBriefcase className="w-4 h-4" />,
};

// Timeline Component
const OrderTimeline = ({ timeline }) => {
  if (!timeline || timeline.length === 0) {
    return <p className="text-gray-500 text-center py-8">No timeline data available</p>;
  }

  const getIcon = (action) => {
    switch (action) {
      case 'created':
        return <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center"><FiCheckCircle className="text-blue-500" /></div>;
      case 'status_changed':
        return <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center"><FiRefreshCw className="text-yellow-500" /></div>;
      case 'assigned':
        return <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center"><FiUserCheck className="text-purple-500" /></div>;
      case 'note_added':
        return <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"><FiMessageSquare className="text-gray-500" /></div>;
      case 'payment_received':
        return <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center"><FiDollarSign className="text-green-500" /></div>;
      default:
        return <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"><FiActivity className="text-gray-500" /></div>;
    }
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="relative">
      {timeline.map((event, index) => (
        <div key={index} className="flex mb-6 last:mb-0">
          <div className="flex-shrink-0 mr-4">
            {getIcon(event.action)}
          </div>
          <div className="flex-1">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-medium text-gray-800">
                    {event.description || `${event.action} order`}
                  </p>
                  {event.changed_by && (
                    <p className="text-xs text-gray-500">
                      by {event.user_name || event.changed_by}
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-400">
                  {formatDateTime(event.created_at)}
                </p>
              </div>
              {event.notes && (
                <p className="text-sm text-gray-600 mt-2 border-t pt-2">{event.notes}</p>
              )}
              {event.previous_status && event.new_status && (
                <div className="flex items-center mt-2 text-sm">
                  <span className={`px-2 py-1 text-xs rounded-full ${statusColors[event.previous_status] || 'bg-gray-100'}`}>
                    {event.previous_status}
                  </span>
                  <FiArrowRight className="mx-2 text-gray-400" />
                  <span className={`px-2 py-1 text-xs rounded-full ${statusColors[event.new_status] || 'bg-gray-100'}`}>
                    {event.new_status}
                  </span>
                </div>
              )}
              {event.assignment && (
                <div className="mt-2 text-sm">
                  <p className="text-gray-600">
                    Assigned: <span className="font-medium">{event.assignment.name}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignType, setAssignType] = useState('driver');
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [tourGuides, setTourGuides] = useState([]);
  const [tourCompanies, setTourCompanies] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);

  useEffect(() => {
    fetchOrder();
    fetchAssignables();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const data = await getOrderById(id);
      setOrder(data.data);
    } catch (error) {
      toast.error('Failed to fetch order details');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignables = async () => {
    try {
      const [driversData, guidesData, companiesData] = await Promise.all([
        getDrivers({ status: 'available', limit: 100 }),
        getTourGuides({ status: 'available', limit: 100 }),
        getCompanies({ status: 'active', limit: 100 })
      ]);
      setDrivers(driversData.data || []);
      setTourGuides(guidesData.data || []);
      setTourCompanies(companiesData.data || []);
    } catch (error) {
      console.error('Failed to fetch assignables:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await updateOrderStatus(id, newStatus, `Status updated to ${newStatus}`);
      toast.success('Order status updated');
      fetchOrder();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleAssign = async (assignment) => {
    try {
      await assignOrder(id, assignment);
      toast.success(`Added ${assignment.type === 'driver' ? 'driver' : assignment.type === 'tour_guide' ? 'tour guide' : 'tour company'} successfully`);
      setShowAssignModal(false);
      fetchOrder();
    } catch (error) {
      toast.error('Failed to assign');
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to remove this assignment?')) return;
    try {
      await removeAssignment(id, assignmentId);
      toast.success('Assignment removed');
      fetchOrder();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  const handleUpdateAssignment = async (assignmentId, data) => {
    try {
      await updateAssignment(id, assignmentId, data);
      toast.success('Assignment updated');
      setEditingAssignment(null);
      fetchOrder();
    } catch (error) {
      toast.error('Failed to update assignment');
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await addOrderNote(id, noteText);
      toast.success('Note added');
      setNoteText('');
      fetchOrder();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleSendReminder = async () => {
    if (!window.confirm(`Send reminder to ${order.customer?.name} for order #${order.order_number}?`)) return;
    setSendingReminder(true);
    try {
      await sendOrderReminder(id);
      toast.success('Reminder sent successfully!');
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAssignmentIcon = (type) => {
    switch (type) {
      case 'driver': return <FiTruck className="w-5 h-5 text-blue-500" />;
      case 'tour_guide': return <FiStar className="w-5 h-5 text-purple-500" />;
      case 'tour_company': return <FiBriefcase className="w-5 h-5 text-green-500" />;
      default: return null;
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!order) return <div className="p-6 text-center">Order not found</div>;

  return (
    <div className="p-6 print:p-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/orders')}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Order #{order.order_number}</h1>
            <p className="text-sm text-gray-500">Created {formatDateTime(order.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleSendReminder}
            disabled={sendingReminder}
            className="flex items-center px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition disabled:opacity-50"
            title="Send Reminder"
          >
            <FiBell className="mr-2" /> Reminder
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
            title="Print"
          >
            <FiPrinter className="mr-2" /> Print
          </button>
          <select
            value={order.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={updating}
            className={`px-3 py-2 rounded-lg border-0 font-medium focus:ring-2 focus:ring-primary-500 ${statusColors[order.status]}`}
          >
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 print:block">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info */}
          <div className="bg-white rounded-xl shadow-sm p-6 print:shadow-none print:border">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FiUser className="mr-2 text-primary-500" /> Customer Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{order.customer?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium">{order.customer?.phone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{order.customer?.email || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Hotel</p>
                <p className="font-medium">{order.customer?.hotel_name || '-'}</p>
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="bg-white rounded-xl shadow-sm p-6 print:shadow-none print:border">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Service Details</h2>
{order.items?.map((item, idx) => (
  <div key={idx} className="border-b last:border-0 pb-4 mb-4 last:pb-0">
    <div className="flex justify-between items-start">
      <div>
        <h3 className="font-semibold text-gray-800">{item.service_name}</h3>
        <p className="text-sm text-gray-500 mt-1">
          {item.price_type === 'private' ? 'Private Service' : 
           item.price_type === 'group' ? 'Group Tour' : 'Small Group Tour'}
        </p>
        {/* عرض تفاصيل المشاركين حسب الفئات */}
        {(item.adults > 0 || item.children > 0 || item.toddlers > 0) && (
          <div className="mt-2 text-sm text-gray-600">
            <p className="font-medium text-gray-700">Participants:</p>
            {item.adults > 0 && <p>👤 Adults (12+): {item.adults} × ${item.adult_price}</p>}
            {item.children > 0 && <p>🧒 Children (6-12): {item.children} × ${item.child_price}</p>}
            {item.toddlers > 0 && <p>👶 Toddlers (2-6): {item.toddlers} × ${item.toddler_price}</p>}
          </div>
        )}
      </div>
      <p className="font-bold text-primary-600">${item.subtotal}</p>
    </div>
  </div>
))}
            
            {/* Discount Section */}
            {order.discount && order.discount.amount > 0 && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-between items-center text-green-600">
                  <span className="flex items-center gap-1"><FiGift /> Discount ({order.discount.code})</span>
                  <span>-${order.discount.amount}</span>
                </div>
              </div>
            )}
            
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total</span>
                <span className="text-xl font-bold text-primary-600">${order.total_amount} {order.currency}</span>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="bg-white rounded-xl shadow-sm p-6 print:shadow-none print:border">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FiCalendar className="mr-2 text-primary-500" /> Schedule
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-medium">{order.schedule?.date ? formatDate(order.schedule.date) : '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Time</p>
                <p className="font-medium">{order.schedule?.time || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Pickup Location</p>
                <p className="font-medium flex items-center"><FiMapPin className="mr-1 text-gray-400" /> {order.schedule?.pickup_location || '-'}</p>
              </div>
            </div>
          </div>

          {/* Assignments (Multiple) */}
          <div className="bg-white rounded-xl shadow-sm p-6 print:shadow-none print:border">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FiTruck className="mr-2 text-primary-500" /> Assignments
            </h2>
            
            {order.assignments && order.assignments.length > 0 ? (
              <div className="space-y-3 mb-4">
                {order.assignments.map((assignment, idx) => (
                  <div key={assignment._id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center">
                        {getAssignmentIcon(assignment.type)}
                        <div className="ml-2">
                          <p className="font-semibold text-gray-800">{assignment.name}</p>
                          <p className="text-sm text-gray-500">{assignment.phone}</p>
                          {assignment.cost > 0 && (
                            <p className="text-xs text-gray-500 mt-1">Cost: ${assignment.cost}</p>
                          )}
                          {assignment.notes && (
                            <p className="text-xs text-gray-400 mt-1">Note: {assignment.notes}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">Assigned: {formatDateTime(assignment.assigned_at)}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingAssignment(assignment)}
                          className="text-blue-500 hover:text-blue-700"
                          title="Edit notes"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRemoveAssignment(assignment._id)}
                          className="text-red-500 hover:text-red-700"
                          title="Remove"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 mb-4">No assignments yet</p>
            )}
            
            <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
              <button
                onClick={() => { setAssignType('driver'); setShowAssignModal(true); }}
                className="flex items-center px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
              >
                <FiTruck className="mr-2" /> Add Driver
              </button>
              <button
                onClick={() => { setAssignType('tour_guide'); setShowAssignModal(true); }}
                className="flex items-center px-3 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition"
              >
                <FiStar className="mr-2" /> Add Tour Guide
              </button>
              <button
                onClick={() => { setAssignType('tour_company'); setShowAssignModal(true); }}
                className="flex items-center px-3 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"
              >
                <FiBriefcase className="mr-2" /> Add Tour Company
              </button>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl shadow-sm p-6 print:shadow-none print:border">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Notes</h2>
            <div className="space-y-3 mb-4">
              {order.notes?.length > 0 ? (
                order.notes.map((note, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-800">{note.text}</p>
                    <p className="text-xs text-gray-400 mt-1">Added by {note.added_by} on {formatDateTime(note.added_at)}</p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm">No notes yet</p>
              )}
            </div>
            <div className="flex space-x-2">
              <input
                type="text"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a note..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
              <button
                onClick={handleAddNote}
                disabled={addingNote || !noteText.trim()}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition"
              >
                {addingNote ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Payment Info */}
          <div className="bg-white rounded-xl shadow-sm p-6 print:shadow-none print:border">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FiDollarSign className="mr-2 text-primary-500" /> Payment
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Method</span>
                <span className="capitalize">{order.payment?.method || 'Cash'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className={`px-2 py-1 rounded-full text-xs ${order.payment?.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {order.payment?.status || 'pending'}
                </span>
              </div>
              {order.payment?.transaction_id && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Transaction</span>
                  <span className="text-sm">{order.payment.transaction_id}</span>
                </div>
              )}
            </div>
          </div>

          {/* Source Info */}
          <div className="bg-white rounded-xl shadow-sm p-6 print:shadow-none print:border">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Source</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Channel</span>
                <span className="capitalize">{order.source}</span>
              </div>
              {order.affiliate_code && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Affiliate Code</span>
                  <span>{order.affiliate_code}</span>
                </div>
              )}
            </div>
          </div>

          {/* Participants */}
          {order.participants && order.participants.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 print:shadow-none print:border">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Participants</h2>
              <div className="space-y-2">
                {order.participants.map((p, idx) => (
                  <div key={idx} className="text-sm">
                    <span className="font-medium">{p.name}</span>
                    {p.age && <span className="text-gray-500 ml-2">({p.age} years)</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transfer Details (for transfer orders) */}
          {order.transfer_details && (
            <div className="bg-white rounded-xl shadow-sm p-6 print:shadow-none print:border">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FiTruck className="mr-2 text-primary-500" /> Transfer Details
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">From</span>
                  <span className="font-medium">{order.transfer_details.from?.replace(/_/g, ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">To</span>
                  <span className="font-medium">{order.transfer_details.to?.replace(/_/g, ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Vehicle</span>
                  <span className="font-medium capitalize">{order.transfer_details.vehicle_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Passengers</span>
                  <span className="font-medium">{order.transfer_details.passengers}</span>
                </div>
              </div>
            </div>
          )}

          {/* Order Timeline */}
          <div className="bg-white rounded-xl shadow-sm p-6 print:shadow-none print:border">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FiClock className="mr-2 text-primary-500" /> Order Timeline
            </h2>
            <OrderTimeline timeline={order.timeline} />
          </div>
        </div>
      </div>

      {/* Assign Modal */}
      <AssignModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onAssign={handleAssign}
        type={assignType}
        drivers={drivers}
        tourGuides={tourGuides}
        tourCompanies={tourCompanies}
      />
    </div>
  );
}
